import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Ledger from "./pages/Ledger";
import Doctors from "./pages/Doctors";
import Customers from "./pages/Customers";
import Compliance from "./pages/Compliance";
import ExpiryDashboard from "./pages/ExpiryDashboard";
import Settings from "./pages/Settings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AdminDashboard from "./pages/AdminDashboard";
import AdminMedicines from "./pages/AdminMedicines";
import DashboardLayout from "./components/Layout";
import "./App.css";

// Reports and HIMS components loaded dynamically to avoid babel plugin issues
import { lazy, Suspense } from "react";
const Reports = lazy(function() { return import("./pages/Reports"); });
const HIMS = lazy(function() { return import("./pages/HIMS"); });

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="flex flex-col items-center gap-4">
          <div className="capsule-icon capsule-icon-large animate-pulse">
            <div className="left"></div>
            <div className="right"></div>
          </div>
          <div className="text-navy-600 font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.is_platform_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/medicines"
            element={
              <AdminRoute>
                <AdminMedicines />
              </AdminRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    <Route index element={<Dashboard />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="ledger" element={<Ledger />} />
                    <Route path="doctors" element={<Doctors />} />
                    <Route path="customers" element={<Customers />} />
                    <Route path="compliance" element={<Compliance />} />
                    <Route path="expiry" element={<ExpiryDashboard />} />
                    <Route path="reports" element={<Suspense fallback={<div className="p-6 text-center">Loading...</div>}><Reports /></Suspense>} />
                    <Route path="hims" element={<Suspense fallback={<div className="p-6 text-center">Loading...</div>}><HIMS /></Suspense>} />
                    <Route path="settings" element={<Settings />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
